import { useEffect, useRef } from 'react';
import { useViewport } from '@xyflow/react';

import {
  COUNTRY_GROUP_STACK_ZOOM_THRESHOLD,
  IS_VC_COUNTRY_GROUPING_ENABLED,
} from '../../../../../../constants/general';
import { applyCountryGroupingToNodes } from '../utils/valueChainNodeLayout';

import type { IValueChainViewportController } from '../interfaces/IValueChainViewportController';

function ValueChainViewportController({
  boardData,
  isScenarioViewMode,
  isInitialViewportReady,
  openAICardPanel,
  isNodeDraggingRef,
  applyViewportCullingToDisplayNodes,
  setNodes,
  onViewportSizeReady,
  onViewportSync,
  onViewportSizeSync,
  onGroupedDataSync,
}: IValueChainViewportController) {
  const { x: viewportX, y: viewportY, zoom: viewportZoom } = useViewport();
  const prevZoomRef = useRef<number | undefined>(undefined);
  const stackingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const viewportCullingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const resizeRafRef = useRef<number | null>(null);
  const lastObservedViewportSizeRef = useRef<{ width: number, height: number } | undefined>(undefined);
  const hasNotifiedViewportSizeReadyRef = useRef(false);
  const viewportSizeRef = useRef<{ width: number, height: number } | undefined>(undefined);
  const groupedDataRef = useRef(boardData);

  useEffect(() => {
    onViewportSync(viewportX, viewportY, viewportZoom);
  }, [onViewportSync, viewportX, viewportY, viewportZoom]);

  useEffect(() => {
    groupedDataRef.current = boardData;
  }, [boardData]);

  useEffect(() => {
    const reactFlowElement = document.querySelector('.reactFlow') as HTMLElement | null;

    if (!reactFlowElement || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const notifyViewportSizeReady = () => {
      if (hasNotifiedViewportSizeReadyRef.current) {
        return;
      }

      hasNotifiedViewportSizeReadyRef.current = true;
      onViewportSizeReady();
    };

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];

      if (!entry) {
        return;
      }

      if (isNodeDraggingRef.current) {
        return;
      }

      const { width, height } = entry.contentRect;

      if (resizeRafRef.current) {
        cancelAnimationFrame(resizeRafRef.current);
      }

      resizeRafRef.current = requestAnimationFrame(() => {
        const nextWidth = Math.round(width);
        const nextHeight = Math.round(height);
        const previousSize = lastObservedViewportSizeRef.current;
        const isSameSize = previousSize
          && previousSize.width === nextWidth
          && previousSize.height === nextHeight;

        if (isSameSize) {
          return;
        }

        const nextSize = {
          width: nextWidth,
          height: nextHeight,
        };

        lastObservedViewportSizeRef.current = nextSize;
        viewportSizeRef.current = nextSize;
        onViewportSizeSync(nextSize);
        notifyViewportSizeReady();
      });
    });

    observer.observe(reactFlowElement);

    const initialSize = {
      width: Math.round(reactFlowElement.clientWidth),
      height: Math.round(reactFlowElement.clientHeight),
    };

    lastObservedViewportSizeRef.current = initialSize;
    viewportSizeRef.current = initialSize;
    onViewportSizeSync(initialSize);
    notifyViewportSizeReady();

    return () => {
      if (resizeRafRef.current) {
        cancelAnimationFrame(resizeRafRef.current);
        resizeRafRef.current = null;
      }

      observer.disconnect();
    };
  }, [isNodeDraggingRef, onViewportSizeReady, onViewportSizeSync]);

  useEffect(() => {
    const viewportSize = viewportSizeRef.current;

    if (!viewportSize || viewportZoom <= 0) {
      return undefined;
    }

    if (!IS_VC_COUNTRY_GROUPING_ENABLED || isScenarioViewMode) {
      return undefined;
    }

    const previousZoom = prevZoomRef.current;
    const isZoomingOut = previousZoom === undefined || viewportZoom < previousZoom;
    const isStackingZoomReached = viewportZoom <= COUNTRY_GROUP_STACK_ZOOM_THRESHOLD;

    prevZoomRef.current = viewportZoom;

    const shouldGroupInitial = Boolean(
      boardData && groupedDataRef.current !== boardData && isStackingZoomReached,
    );
    const shouldUpdateCountryGroups = isZoomingOut && isStackingZoomReached;

    if (!shouldUpdateCountryGroups && !shouldGroupInitial) {
      return undefined;
    }

    if (stackingTimerRef.current) {
      clearTimeout(stackingTimerRef.current);
    }

    stackingTimerRef.current = setTimeout(() => {
      groupedDataRef.current = boardData;
      onGroupedDataSync(boardData);

      const { height } = viewportSize;
      const worldViewportTop = -viewportY / viewportZoom;
      const worldViewportBottom = (height - viewportY) / viewportZoom;

      setNodes((prevNodes) => applyViewportCullingToDisplayNodes(
        applyCountryGroupingToNodes(
          prevNodes,
          worldViewportTop,
          worldViewportBottom,
        ),
      ));
    }, 150);

    return () => {
      if (stackingTimerRef.current) {
        clearTimeout(stackingTimerRef.current);
      }
    };
  }, [
    applyViewportCullingToDisplayNodes,
    boardData,
    isScenarioViewMode,
    onGroupedDataSync,
    setNodes,
    viewportY,
    viewportZoom,
  ]);

  useEffect(() => {
    const viewportSize = viewportSizeRef.current;

    if (!isInitialViewportReady || !viewportSize || viewportZoom <= 0) {
      return undefined;
    }

    if (viewportCullingTimerRef.current) {
      clearTimeout(viewportCullingTimerRef.current);
    }

    viewportCullingTimerRef.current = setTimeout(() => {
      if (isNodeDraggingRef.current) {
        return;
      }

      setNodes((prevNodes) => applyViewportCullingToDisplayNodes(prevNodes));
    }, 100);

    return () => {
      if (viewportCullingTimerRef.current) {
        clearTimeout(viewportCullingTimerRef.current);
      }
    };
  }, [
    applyViewportCullingToDisplayNodes,
    isInitialViewportReady,
    isNodeDraggingRef,
    setNodes,
    viewportX,
    viewportY,
    viewportZoom,
  ]);

  useEffect(() => {
    if (!openAICardPanel || !stackingTimerRef.current) {
      return;
    }

    clearTimeout(stackingTimerRef.current);
    stackingTimerRef.current = null;
  }, [openAICardPanel]);

  return null;
}

export default ValueChainViewportController;
